import axios from 'axios';

const ROOT_URL = '/api';

const api = axios.create({
  baseURL: ROOT_URL,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = token;
  }
  return config;
});

export default api;

// ==================== Auth API ====================

export function getToken(email: string, password: string) {
  return api.post('/token/generate', { email, password });
}

export function validateToken(token: string) {
  return api.post('/token/is_valid', { token });
}

export function createUser(email: string, password: string) {
  return api.post('/user/create', { email, password });
}

export function getCurrentUser() {
  return api.get('/user/current');
}

export function getUserPreferences() {
  return api.get('/user/current/preferences');
}

export function updateUserPreferences(data: Record<string, unknown>) {
  return api.put('/user/current/preferences', data);
}

// ==================== Stats API ====================

export function fetchLocationBalanceStats(fromDate: string, toDate: string) {
  return api.get(`/stats/balance/from/${fromDate}/to/${toDate}`);
}

export function fetchDeliveryMenStats(fromDate: string, toDate: string) {
  return api.get(`/stats/delivery_man/from/${fromDate}/to/${toDate}`);
}

export function fetchItemsSoldStats(fromDate: string, toDate: string) {
  return api.get(`/stats/items_sold/from/${fromDate}/to/${toDate}`);
}

export function printDeliverymanStats(deliverymanId: number, fromDate: string, toDate: string, locationId: number) {
  return api.post(`/deliveryman/${deliverymanId}/print_stats/from/${fromDate}/to/${toDate}`, { location_id: locationId });
}

// ==================== Trends API ====================

import type {
  TrendsRevenueResponse,
  TrendsOperationsResponse,
  TrendsItemsResponse,
  TrendsCallsResponse,
  TrendsCustomersResponse,
  TrendsPersonnelResponse,
} from '../types';

export interface TrendsParams {
  from: string;
  to: string;
  granularity?: 'daily' | 'weekly';
}

export function fetchTrendsRevenue(params: TrendsParams) {
  return api.get<TrendsRevenueResponse>('/stats/trends/revenue', { params });
}

export function fetchTrendsOperations(params: TrendsParams) {
  return api.get<TrendsOperationsResponse>('/stats/trends/operations', { params });
}

export function fetchTrendsItems(params: TrendsParams) {
  return api.get<TrendsItemsResponse>('/stats/trends/items', { params });
}

export function fetchTrendsCalls(params: TrendsParams) {
  return api.get<TrendsCallsResponse>('/stats/trends/calls', { params });
}

export function fetchTrendsCustomers(params: TrendsParams) {
  return api.get<TrendsCustomersResponse>('/stats/trends/customers', { params });
}

export function fetchTrendsPersonnel(params: TrendsParams) {
  return api.get<TrendsPersonnelResponse>('/stats/trends/personnel', { params });
}

// ==================== Delivery API ====================

export function fetchDeliveriesByDate(date: string) {
  return api.get(`/delivery/date/${date}`);
}

export function cancelDelivery(deliveryId: number, canceledReason: string) {
  return api.put(`/delivery/${deliveryId}/cancel`, { canceled_reason: canceledReason });
}

export function uncancelDelivery(deliveryId: number) {
  return api.put(`/delivery/${deliveryId}/uncancel`);
}

export function printDeliveryTicket(deliveryId: number) {
  return api.put(`/delivery/${deliveryId}/printTicket`);
}

export function removeDeliveryFromHandoff(deliveryId: number) {
  return api.put(`/delivery/undo/assign/${deliveryId}`);
}

export function approveDelivery(deliveryId: number, estimatedDeliveryMinutes: number | null) {
  // Delayed orders are committed to the customer-requested slot, so we send
  // no ETA — backend forces estimated_delivery_minutes to NULL for those.
  const body = estimatedDeliveryMinutes == null ? {} : { estimated_delivery_minutes: estimatedDeliveryMinutes };
  return api.put(`/delivery/${deliveryId}/approve`, body);
}

export function rejectDelivery(deliveryId: number, reason: string) {
  return api.put(`/delivery/${deliveryId}/reject`, { reason });
}

// ==================== Call API ====================

export function fetchCallsByDate(date: string) {
  return api.get(`/call/date/${date}`);
}

import type { CallQuality } from '../types';

export function fetchCallQualityEnabled() {
  return api.get<{ enabled: boolean }>('/call/quality-enabled');
}

export function fetchCallQuality(uuid: string) {
  return api.get<CallQuality | { quality: null }>(`/call/quality/${uuid}`);
}

// ==================== Business API ====================

export interface OnlineWorkingHoursEntry {
  day: number;
  open: string;
  close: string;
  active: boolean;
}

export interface Business {
  id: number;
  name: string;
  address: string;
  city: string;
  number: string;
  description: string;
  cfg_timezone?: string;
  cfg_print_service?: string;
  cfg_google_token?: string;
  cfg_google_server_key?: string;
  cfg_delivery_barcode_prefix?: string;
  cfg_deliveryman_barcode_prefix?: string;
  cfg_barcode_x_offset?: number;
  cfg_stripe_secret_key?: string;
  cfg_stripe_publishable_key?: string;
  cfg_stripe_webhook_secret?: string;
  cfg_payment_capture_on_approval?: boolean;
  cfg_refund_on_cancel?: boolean;
  cfg_online_order_location_id?: number | null;
  cfg_online_order_sound?: boolean;
  cfg_online_order_sound_type?: string;
  cfg_plivoman_url?: string;
  cfg_plivoman_api_key?: string;
  cfg_online_working_hours?: OnlineWorkingHoursEntry[] | null;
  cfg_online_orders_paused?: boolean;
  cfg_discount_levels?: string | null;
  cfg_printer_url?: string;
  cfg_local_country_prefix?: string;
  cfg_cdn_provider?: string;
  cfg_cdn_cloud_name?: string;
  cfg_cdn_api_key?: string;
  cfg_cdn_api_secret?: string;
  cfg_currency?: string;
  cfg_bodovi_calculation?: string;
  cfg_delivery_modulo?: number | null;
  cfg_widget_theme?: string;
  cfg_min_delivery_time?: number | null;
  cfg_show_min_delivery_time?: boolean;
  cfg_widget_hero_image?: string | null;
  cfg_widget_url?: string | null;
  cfg_reservations_enabled?: boolean;
  cfg_reservation_working_hours?: OnlineWorkingHoursEntry[] | null;
  cfg_reservation_printer_output_id?: number | null;
  cfg_show_deliveryman_stats?: boolean;
  cfg_allow_non_admin_add_deliverymen?: boolean;
  cfg_show_scanner_indicator?: boolean;
  cfg_restaurant_reply_to_email?: string | null;
  cfg_restaurant_website?: string | null;
  cfg_email_template_submit?: string | null;
  cfg_email_template_approve?: string | null;
  cfg_email_template_reject?: string | null;
  cfg_google_maps_static_key?: string | null;
}

export interface Reservation {
  id: number;
  uuid: string;
  customer_name: string | null;
  contact_phone: string | null;
  email: string | null;
  reservation_date: string | null;
  reservation_time: string | null;
  guests: number | null;
  note: string | null;
  approved: boolean | null;
  approved_at: string | null;
  rejected_reason: string | null;
  printed: boolean;
  created_at: string | null;
}

export function fetchReservations(fromDate: string, toDate: string) {
  return api.get<Reservation[]>(`/reservation/?from=${fromDate}&to=${toDate}`);
}

export function approveReservation(id: number) {
  return api.put<Reservation>(`/reservation/${id}/approve`);
}

export function reprintReservation(id: number) {
  return api.post(`/reservation/${id}/print`);
}

export function createManualReservation(data: {
  customer_name: string;
  contact_phone: string;
  reservation_date: string;
  reservation_time: string;
  guests: number;
  note?: string;
}) {
  return api.post<Reservation>(`/reservation/manual`, data);
}

export function rejectReservation(id: number, reason: string) {
  return api.put<Reservation>(`/reservation/${id}/reject`, { reason });
}

export function toggleOnlineOrders(paused: boolean) {
  return api.post('/business/online-orders/toggle', { paused });
}

export function updateMinDeliveryTime(data: { minutes?: number | null; show?: boolean }) {
  return api.post('/business/online-orders/min-delivery-time', data);
}

export function fetchOnlineOrdersToggleHistory() {
  return api.get('/business/online-orders/toggle-history');
}

export function fetchBusiness() {
  return api.get<Business[]>('/business/');
}

export function fetchBusinessById(id: number) {
  return api.get<Business>(`/business/${id}`);
}

export function updateBusiness(id: number, data: Partial<Business>) {
  return api.put<Business>(`/business/${id}`, { ...data, id });
}

// ==================== Location API ====================

import type { Location } from '../types';

export function fetchLocations() {
  return api.get<Location[]>('/location/');
}

export function createLocation(data: { name: string; address?: string; description?: string; requires_delivery?: boolean; active?: boolean }) {
  return api.post<Location>('/location/', data);
}

export function updateLocation(id: number, data: Partial<Location> & { id: number }) {
  return api.put<Location>(`/location/${id}`, data);
}

export function deleteLocation(id: number) {
  return api.delete(`/location/${id}`, { data: { id } });
}

// ==================== Menu Image Upload ====================

export type ImageEntity = 'business' | 'menu_group' | 'menu_item';

export interface UploadMenuImageOpts {
  entity: ImageEntity;
  // The entity's id, when known. For "create new <entity>" flows where the
  // entity hasn't been saved yet, leave undefined — the backend uses a
  // UUID-suffixed public_id so concurrent creators never collide.
  entityId?: number | null;
  // Prior image URL for this slot, if any. The backend best-effort deletes
  // it from the CDN after a successful upload to avoid orphan assets.
  previousUrl?: string | null;
}

export function uploadMenuImage(file: File, opts: UploadMenuImageOpts) {
  const formData = new FormData();
  formData.append('image', file);
  if (opts.previousUrl) formData.append('previous_url', opts.previousUrl);
  const params = new URLSearchParams({ entity: opts.entity });
  if (opts.entityId != null) params.set('id', String(opts.entityId));
  return api.post<{ url: string }>(`/menu/upload?${params.toString()}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

// ==================== Business Logo (ZPL ticket) ====================

export function uploadBusinessLogo(file: File) {
  const formData = new FormData();
  formData.append('image', file);
  return api.post<{ width: number; height: number; bytes: number }>(
    '/business/logo',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
}

export function deleteBusinessLogo() {
  return api.delete('/business/logo');
}

// ==================== Ticket Layout Builder ====================

export type TicketLayoutPropKind = 'font_h' | 'bool' | 'text' | 'align';

export interface TicketLayoutSectionProp {
  name: string;
  kind: TicketLayoutPropKind;
  default: unknown;
  min?: number;
  max?: number;
}

export interface TicketLayoutSectionType {
  type: string;
  label: string;
  singleton: boolean;
  // Legally-required sections (currently footer + disclaimer) the user
  // can't hide, reorder, or edit. The backend re-stamps defaults on
  // every save, so this flag is purely a UI affordance hint.
  locked?: boolean;
  conditional?: string;
  props: TicketLayoutSectionProp[];
}

export interface TicketLayoutSection {
  id: string;
  type: string;
  visible: boolean;
  [key: string]: unknown;
}

export interface TicketLayout {
  version: number;
  sections: TicketLayoutSection[];
}

export function fetchTicketLayout() {
  return api.get<{ layout: TicketLayout; is_default: boolean }>('/business/ticket-layout/');
}

export function saveTicketLayout(layout: TicketLayout | null) {
  return api.put<{ layout: TicketLayout; is_default: boolean }>(
    '/business/ticket-layout/',
    { layout },
  );
}

export function resetTicketLayout() {
  return api.delete<{ layout: TicketLayout; is_default: boolean }>('/business/ticket-layout/');
}

export function fetchTicketLayoutSections() {
  return api.get<{ sections: TicketLayoutSectionType[] }>('/business/ticket-layout/sections');
}

export interface TicketSampleOrder {
  count: number;
  gratis: boolean;
  note: string;
  menu_item: { name: string; short_name: string | null; price: number };
  addons: TicketSampleOrder[];
}

export interface TicketSampleDelivery {
  id: number;
  amount: string | number;
  discount: number;
  cc_payment: boolean;
  online_paid: boolean;
  online_order: boolean;
  approved: boolean;
  estimated_delivery_minutes: number | null;
  delayed: boolean;
  delayed_time: string | null;
  note: string;
  created_at: string;
  barcode_x_offset: number;
  currency: string;
  delivery_modulo: number | null;
  orders: TicketSampleOrder[];
  customer_address: {
    name: string;
    address: string;
    floor: string;
    contact_phone: string;
    extra: string;
    customer: { phone_number: { phone_number: string } } | null;
  };
}

export function fetchTicketLayoutSampleDelivery() {
  return api.get<TicketSampleDelivery>('/business/ticket-layout/sample-delivery');
}

// Fetch the preview as a blob via the authenticated axios instance; the UI
// turns it into an object URL for an <img> tag so the JWT never has to live
// in the URL string.
export function fetchBusinessLogoBlob() {
  return api.get<Blob>('/business/logo.png', { responseType: 'blob' });
}

// ==================== Menu API ====================

// Menu Items
export function createMenuItem(data: Record<string, unknown>) {
  return api.post('/menu/item', data);
}

export function createMenuItemInGroup(groupId: number, data: Record<string, unknown>) {
  return api.post(`/menu/item/in/group/${groupId}`, data);
}

export function updateMenuItem(itemId: number, data: Record<string, unknown>) {
  return api.put(`/menu/item/${itemId}`, data);
}

export function deleteMenuItem(itemId: number) {
  return api.delete(`/menu/item/${itemId}`);
}

// Menu Groups
export function createMenuGroup(data: Record<string, unknown>) {
  return api.post('/menu/group', data);
}

export function updateMenuGroup(groupId: number, data: Record<string, unknown>) {
  return api.put(`/menu/group/${groupId}`, data);
}

export function deleteMenuGroup(groupId: number) {
  return api.delete(`/menu/group/${groupId}`);
}

// Group Membership
export function addItemToGroup(itemId: number, groupId: number) {
  return api.post(`/menu/item/${itemId}/group/${groupId}/member`);
}

export function removeItemFromGroup(itemId: number, groupId: number) {
  return api.delete(`/menu/item/${itemId}/group/${groupId}/member`);
}

// Group-Group Complements
export function addGroupGroupComplement(groupId: number, complementGroupId: number) {
  return api.post(`/menu/group/${groupId}/group/${complementGroupId}/complement`);
}

export function removeGroupGroupComplement(groupId: number, complementGroupId: number) {
  return api.delete(`/menu/group/${groupId}/group/${complementGroupId}/complement`);
}

// Item-Group Complements
export function addItemGroupComplement(itemId: number, complementGroupId: number) {
  return api.post(`/menu/item/${itemId}/group/${complementGroupId}/complement`);
}

export function removeItemGroupComplement(itemId: number, complementGroupId: number) {
  return api.delete(`/menu/item/${itemId}/group/${complementGroupId}/complement`);
}

// Create group as complement
export function createMenuGroupAsGroupComplement(parentGroupId: number, data: Record<string, unknown>) {
  return api.post(`/menu/group/complements/group/${parentGroupId}`, data);
}

// ==================== Deliveryman API ====================

export function fetchDeliverymen() {
  return api.get('/deliveryman/');
}

export function createDeliveryman(data: { name: string; vehicle?: string; active?: boolean }) {
  return api.post('/deliveryman/', data);
}

export function updateDeliveryman(id: number, data: { id: number; name: string; vehicle?: string; active?: boolean }) {
  return api.put(`/deliveryman/${id}`, data);
}

export function deleteDeliveryman(id: number) {
  return api.delete(`/deliveryman/${id}`, { data: { id } });
}

export function printDeliverymanCard(deliverymanId: number, locationId: number) {
  return api.post('/deliveryman/print_card/action/', { deliveryman_id: deliverymanId, location_id: locationId });
}

// ==================== IVR API ====================

export interface IVRRule {
  id: number;
  customer_id: number;
  day: string;
  time_from: string;
  time_to: string;
  mode: string;
  args: string | null;
  priority: number;
  is_active: boolean;
}

export interface IVROverride {
  id: number;
  customer_id: number;
  date: string;
  time_from: string;
  time_to: string;
  mode: string;
  args: string | null;
  priority: number;
  description: string | null;
  is_active: boolean;
}

export interface IVRCustomer {
  name: string;
  number: string;
  day_off_mode: string | null;
  day_off_args: string | null;
}

export function fetchIVRRules(day?: string) {
  const params = day ? `?day=${day}` : '';
  return api.get<IVRRule[]>(`/ivr/rules${params}`);
}

export function createIVRRule(data: Partial<IVRRule>) {
  return api.post<IVRRule>('/ivr/rules', data);
}

export function updateIVRRule(id: number, data: Partial<IVRRule>) {
  return api.put<IVRRule>(`/ivr/rules/${id}`, data);
}

export function deleteIVRRule(id: number) {
  return api.delete(`/ivr/rules/${id}`);
}

export function fetchIVROverrides() {
  return api.get<IVROverride[]>('/ivr/overrides');
}

export function createIVROverride(data: Partial<IVROverride>) {
  return api.post<IVROverride>('/ivr/overrides', data);
}

export function updateIVROverride(id: number, data: Partial<IVROverride>) {
  return api.put<IVROverride>(`/ivr/overrides/${id}`, data);
}

export function deleteIVROverride(id: number) {
  return api.delete(`/ivr/overrides/${id}`);
}

export function fetchIVRCustomer() {
  return api.get<IVRCustomer>('/ivr/customer');
}

export interface IVRPreviewPrompt {
  url: string;
  label: string;
}

export interface IVRPreviewResponse {
  prompts: IVRPreviewPrompt[];
  action: string;
  action_label: string;
}

export function fetchIVRPreviewPrompts(mode: string, args?: string) {
  return api.get<IVRPreviewResponse>('/ivr/preview-prompts', {
    params: { mode, args: args || '' },
  });
}

export interface IVRDayOffPreset {
  day_off_mode: string | null;
  day_off_args: string | null;
  configured: boolean;
  has_day_off_rules: boolean;
}

export function fetchIVRDayOffPreset() {
  return api.get<IVRDayOffPreset>('/ivr/day-off-preset');
}

export function createIVRDayOff(date: string, description?: string) {
  return api.post('/ivr/day-off', { date, description: description || 'Neradni dan' });
}

export function deleteIVROverridesByDate(date: string) {
  return api.delete(`/ivr/overrides/by-date/${date}`);
}

// ==================== Delivery Zone API ====================

export interface DeliveryZoneFeeItem {
  id: number;
  name: string;
  price: number;
}

export interface DeliveryZone {
  id: number;
  name: string;
  delivery_fee_item_id: number | null;
  delivery_fee_item: DeliveryZoneFeeItem | null;
  minimum_order: number | null;
  zone_type: string | null;
  shape_json: Array<{ lat: number; lng: number }> | null;
  color: string | null;
}

export function fetchDeliveryZones() {
  return api.get<DeliveryZone[]>('/delivery_zone/');
}

export function createDeliveryZone(data: Partial<DeliveryZone>) {
  return api.post<DeliveryZone>('/delivery_zone/', data);
}

export function updateDeliveryZone(id: number, data: Partial<DeliveryZone>) {
  return api.put<DeliveryZone>(`/delivery_zone/${id}`, data);
}

export function deleteDeliveryZone(id: number) {
  return api.delete(`/delivery_zone/${id}`);
}

// ==================== Agent API ====================

export interface Agent {
  id: number;
  agent_name: string;
  agent_key: string;
}

export function fetchAgents() {
  return api.get<Agent[]>('/agent/');
}

export function createAgent(data: { agent_name: string; agent_key: string }) {
  return api.post<Agent>('/agent/', data);
}

export function updateAgent(id: number, data: { agent_name: string; agent_key: string }) {
  return api.put<Agent>(`/agent/${id}`, data);
}

export function deleteAgent(id: number) {
  return api.delete(`/agent/${id}`);
}

// Agent Destinations
export interface AgentDestination {
  location_id: number;
  default_location: boolean;
}

export function fetchAgentDestinations(agentId: number) {
  return api.get<AgentDestination[]>(`/agent/${agentId}/destinations`);
}

export function updateAgentDestinations(agentId: number, destinations: AgentDestination[]) {
  return api.put<AgentDestination[]>(`/agent/${agentId}/destinations`, destinations);
}

// ==================== User API ====================

export interface UserRecord {
  id: number;
  email: string;
  admin: boolean;
  active: boolean;
  agent_id: number | null;
  ui_delay: boolean;
  ui_gratis: boolean;
  ui_nth_gratis: boolean;
  ui_loyalty: boolean;
  ui_mini_stats: boolean;
  ui_distance: boolean;
  ui_discount: boolean;
  ui_payment_opts: boolean;
  ui_map_overlay: boolean;
  ui_call_stats: boolean;
  ui_tablet_view: boolean;
  ui_dialed_number_filter: boolean;
}

export function fetchUsers() {
  return api.get<UserRecord[]>('/user/');
}

export function assignAgentToUser(userId: number, agentId: number) {
  return api.put(`/user/user/${userId}/agent_key/${agentId}/assign`);
}

export function clearAgentFromUser(userId: number) {
  return api.put(`/user/user/${userId}/clear_agent`);
}

export function updateUser(userId: number, data: Partial<Omit<UserRecord, 'id' | 'agent_id'>>) {
  return api.put<UserRecord>(`/user/${userId}`, data);
}

export function deleteUser(userId: number) {
  return api.delete(`/user/${userId}`);
}

export function resetUserPassword(userId: number, newPassword: string) {
  return api.put(`/user/${userId}/reset_password`, { new_password: newPassword });
}

// ==================== Output API ====================

export interface Output {
  id: number;
  model: string;
  sn: string;
  online: boolean;
  problem: boolean;
  printer: boolean;
  location: number | null;
  location_id: number | null;
  menu_group_id: number | null;
  bt_address: string | null;
}

export function fetchOutputs() {
  return api.get<Output[]>('/output/');
}

export function createOutput(data: Partial<Output>) {
  return api.post<Output>('/output/', data);
}

export function updateOutput(id: number, data: Partial<Output>) {
  return api.put<Output>(`/output/${id}`, data);
}

export function deleteOutput(id: number) {
  return api.delete(`/output/${id}`);
}

export function printScannerPairing(outputId: number) {
  return api.post(`/output/${outputId}/print_scanner_pairing`);
}

export function fetchPrinterState() {
  return api.get<import('../store/slices/printerSlice').PrinterState[]>('/printer/state');
}
